import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * StockReservations Component
 * 
 * Manages stock_reservations table (Many-to-One relationship to products)
 * Tracks inventory that's reserved for:
 * - Sales orders
 * - Production orders
 * - Transfers
 * - Custom reservations
 */
const StockReservations = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [filters, setFilters] = useState({
    productId: '',
    warehouseId: '',
    status: '',
    reservationType: ''
  });

  useEffect(() => {
    loadColumnSettings();
    loadProducts();
    loadWarehouses();
    loadReservations();
  }, [organization]);

  const loadProducts = async () => {
    try {
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await inventoryApi.getWarehouses(organization);
      const warehousesData = Array.isArray(response) ? response : (response.data || []);
      setWarehouses(warehousesData);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const loadReservations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await inventoryApi.getStockReservations(
        organization,
        filters.productId || null,
        filters.warehouseId || null,
        filters.status || null,
        filters.reservationType || null
      );
      
      const reservationsData = Array.isArray(response) ? response : (response.data || []);
      setReservations(reservationsData);

      // Generate dynamic columns
      if (reservationsData.length > 0) {
        const saved = localStorage.getItem(`stockReservationsColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = reservationsData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      setError(err.message || 'Failed to load stock reservations');
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`stockReservationsColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`stockReservationsColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    saveColumnSettings(columnSettings, newSort);
  };

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`stockReservationsColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    if (reservations.length > 0) {
      const firstItem = reservations[0];
      const columns = Object.keys(firstItem)
        .filter(key => key !== 'id')
        .map((key, index) => ({
          key,
          label: formatColumnLabel(key),
          visible: true,
          order: index
        }));
      setTableColumns(columns);
    }
  };

  // Format cell values
  const formatCellValue = (value, key, reservation) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle product_id
    if (key === 'product_id' || key === 'productId') {
      return getProductName(value);
    }
    
    // Handle warehouse_id
    if (key === 'warehouse_id' || key === 'warehouseId') {
      return getWarehouseName(value);
    }
    
    // Handle dates
    if (key.includes('date') || key.includes('Date') || key.includes('_at')) {
      return formatDate(value);
    }
    
    // Handle quantities
    if (key.includes('quantity') || key.includes('Quantity')) {
      return parseFloat(value).toFixed(2);
    }
    
    // Handle status
    if (key === 'status') {
      const status = (value || 'active').toLowerCase();
      return (
        <span className={`pm-status-badge ${getStatusClass(status)}`}>
          {status.toUpperCase()}
        </span>
      );
    }
    
    // Handle reservation_type
    if (key === 'reservation_type' || key === 'reservationType') {
      return (
        <span className="pm-type-badge">
          {(value || 'custom').replace('_', ' ').toUpperCase()}
        </span>
      );
    }
    
    return String(value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    loadReservations();
  };

  const handleClearFilters = () => {
    setFilters({
      productId: '',
      warehouseId: '',
      status: '',
      reservationType: ''
    });
    setTimeout(() => loadReservations(), 100);
  };

  const handleRelease = async (reservationId) => {
    if (!window.confirm(t('inventory.release_reservation_tooltip') + '?')) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.releaseStockReservation(reservationId);
      await loadReservations();
    } catch (err) {
      setError(err.message || 'Failed to release reservation');
    }
  };

  const handleDelete = async (reservationId) => {
    if (!window.confirm(t('inventory.delete_product_confirm'))) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.deleteStockReservation(reservationId);
      await loadReservations();
    } catch (err) {
      setError(err.message || 'Failed to delete reservation');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown';
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'active': 'status-active',
      'fulfilled': 'status-fulfilled',
      'released': 'status-released',
      'expired': 'status-expired',
      'cancelled': 'status-cancelled'
    };
    return statusMap[status?.toLowerCase()] || 'status-other';
  };

  const getTotalReservedQuantity = () => {
    return reservations
      .filter(r => (r.status || '').toLowerCase() === 'active')
      .reduce((sum, r) => sum + parseFloat(r.quantity_reserved || r.quantityReserved || 0), 0);
  };

  if (loading && reservations.length === 0 && !products.length) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading')}
      </div>
    );
  }

  // Filter and search reservations
  const filteredReservations = reservations.filter(reservation => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(reservation).some(value =>
      String(value).toLowerCase().includes(term)
    );
  });

  // Sort reservations
  const sortedReservations = [...filteredReservations].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue === bValue) return 0;
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    const comparison = aValue < bValue ? -1 : 1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const visibleColumns = tableColumns.filter(col => col.visible);

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <div>
          <h2 className="pm-header-title">{t('inventory.stock_reservations_title')}</h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--pico-muted-color)', fontSize: '0.9rem' }}>
            {t('inventory.stock_reservations_description')}
          </p>
        </div>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_reservations_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <select
            name="productId"
            value={filters.productId}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_products_option')}</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.sku || product.SKU || 'N/A'})
              </option>
            ))}
          </select>
          <select
            name="warehouseId"
            value={filters.warehouseId}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_warehouses_option')}</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_statuses_option')}</option>
            <option value="active">{t('inventory.status_active')}</option>
            <option value="fulfilled">{t('inventory.status_fulfilled')}</option>
            <option value="released">{t('inventory.status_released')}</option>
            <option value="expired">{t('inventory.status_expired')}</option>
            <option value="cancelled">{t('inventory.status_cancelled')}</option>
          </select>
          <select
            name="reservationType"
            value={filters.reservationType}
            onChange={handleFilterChange}
          >
            <option value="">{t('inventory.all_types_option')}</option>
            <option value="sales_order">{t('inventory.reservation_type_sales_order')}</option>
            <option value="production">{t('inventory.reservation_type_production')}</option>
            <option value="transfer">{t('inventory.reservation_type_transfer')}</option>
            <option value="custom">{t('inventory.reservation_type_custom')}</option>
          </select>
          <button className="pm-btn-primary" onClick={handleApplyFilters}>
            {t('inventory.apply_filters_button')}
          </button>
          <button className="pm-btn-secondary" onClick={handleClearFilters}>
            {t('inventory.clear_filters_button')}
          </button>
        </div>
      </div>

      <div className="pm-summary-grid">
        <div className="pm-summary-card">
          <h4>{t('inventory.total_reservations')}</h4>
          <p className="pm-summary-value">{reservations.length}</p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.active_reservations_count')}</h4>
          <p className="pm-summary-value">
            {reservations.filter(r => (r.status || '').toLowerCase() === 'active').length}
          </p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.total_reserved_qty')}</h4>
          <p className="pm-summary-value">{getTotalReservedQuantity().toFixed(2)}</p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.fulfilled_count')}</h4>
          <p className="pm-summary-value">
            {reservations.filter(r => (r.status || '').toLowerCase() === 'fulfilled').length}
          </p>
        </div>
      </div>

      <div className="pm-table-wrap">
        <table className="pm-table" role="grid">
          <thead>
            <tr>
              <th style={{ width: '100px' }}>
                <div className="pm-header-content">
                  {tableColumns.filter(c => !c.visible).length > 0 && (
                    <button
                      className="pm-column-btn visibility-btn"
                      onClick={() => setShowColumnMenu(showColumnMenu === 'hidden' ? null : 'hidden')}
                      title={t('inventory.show_hidden_columns_title')}
                    >
                      ⊕
                    </button>
                  )}
                  <div className="pm-header-label">{t('inventory.actions_header')}</div>
                </div>
                {showColumnMenu === 'hidden' && (
                  <div className="pm-column-menu">
                    <div className="menu-header">{t('inventory.hidden_columns_menu_header')}</div>
                    {tableColumns.filter(c => !c.visible).map(col => (
                      <button key={col.key} onClick={() => toggleColumnVisibility(col.key)}>
                        {t('inventory.show_column_button')} {col.label}
                      </button>
                    ))}
                    <hr />
                    <button onClick={() => {
                      resetColumnSettings();
                      setShowColumnMenu(null);
                    }}>
                      {t('inventory.reset_all_button')}
                    </button>
                  </div>
                )}
              </th>
              {visibleColumns.map((col, index) => (
                <th key={col.key} className="pm-sortable-header">
                  <div className="pm-header-content">
                    <div className="pm-header-controls">
                      <button
                        className="pm-column-btn"
                        onClick={() => moveColumnUp(col.key)}
                        disabled={index === 0}
                        title={t('inventory.move_left_title')}
                      >
                        ◀
                      </button>
                      <button
                        className="pm-column-btn"
                        onClick={() => moveColumnDown(col.key)}
                        disabled={index === visibleColumns.length - 1}
                        title={t('inventory.move_right_title')}
                      >
                        ▶
                      </button>
                      <button
                        className="pm-column-btn visibility-btn"
                        onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                        title={t('inventory.column_options_title')}
                      >
                        ⋮
                      </button>
                    </div>
                    <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                      {col.label}
                      {sortConfig.key === col.key && (
                        <span className="pm-sort-indicator">
                          {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </div>
                  </div>
                  {showColumnMenu === col.key && (
                    <div className="pm-column-menu">
                      <button onClick={() => {
                        toggleColumnVisibility(col.key);
                        setShowColumnMenu(null);
                      }}>
                        {t('inventory.hide_column_button')}
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length + 1}>
                  <div className="pm-skeleton" aria-busy="true">{t('inventory.loading')}</div>
                </td>
              </tr>
            ) : sortedReservations.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1}>
                  <div className="pm-empty-state">
                    <div className="pm-empty-state-icon" aria-hidden="true">🔒</div>
                    <h3>{t('inventory.no_stock_reservations_found')}</h3>
                  </div>
                </td>
              </tr>
            ) : (
              sortedReservations.map((reservation) => {
                const status = (reservation.status || 'active').toLowerCase();
                return (
                  <tr key={reservation.id}>
                    <td>
                      <div className="pm-row-actions">
                        {status === 'active' && (
                          <button 
                            className="pm-release-btn"
                            onClick={() => handleRelease(reservation.id)} 
                            title={t('inventory.release_reservation_tooltip')}
                          >
                            ↺
                          </button>
                        )}
                        <button 
                          className="pm-row-btn delete"
                          onClick={() => handleDelete(reservation.id)} 
                          title={t('inventory.delete_tooltip')}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                    {visibleColumns.map(col => (
                      <td key={col.key}>
                        {formatCellValue(reservation[col.key], col.key, reservation)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockReservations;
